import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Paper,
  Collapse,
  ImageList,
  ImageListItem,
  ImageListItemBar,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PhotoLibrary as PhotoLibraryIcon,
  ZoomIn as ZoomInIcon,
  ArrowBackIos as ArrowBackIcon,
  ArrowForwardIos as ArrowForwardIcon,
} from '@mui/icons-material';

import productPhotoStore from '../stores/productPhotoStore';
import { getImageUrl } from '../utils/imageUtils';
import logger from '../utils/logger';

const ProductPhotoGallery = observer(({ open, onClose, productId, productName }) => {
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);
  const [expandedTagGroups, setExpandedTagGroups] = useState({});

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Загружаем фотографии при открытии модального окна
  useEffect(() => {
    if (open && productId) {
      logger.log('ProductPhotoGallery: Загружаю фотографии для продукта', productId);
      productPhotoStore.fetchProductPhotos(productId);
    }
  }, [open, productId]);

  // Очищаем данные при закрытии
  useEffect(() => {
    if (!open) {
      productPhotoStore.clear();
      setSelectedPhoto(null);
      setSelectedPhotoIndex(0);
      setIsPhotoModalOpen(false);
      setExpandedTagGroups({});
    }
  }, [open]);

  const handlePhotoClick = (photo) => {
    const photoIndex = productPhotoStore.filteredPhotos.findIndex(p => p.id === photo.id);
    setSelectedPhoto(photo);
    setSelectedPhotoIndex(photoIndex);
    setIsPhotoModalOpen(true);
  };

  const handleClosePhotoModal = () => {
    setIsPhotoModalOpen(false);
    setSelectedPhoto(null);
    setSelectedPhotoIndex(0);
  };

  const handlePreviousPhoto = () => {
    const currentIndex = selectedPhotoIndex;
    const newIndex = currentIndex > 0 ? currentIndex - 1 : productPhotoStore.filteredPhotos.length - 1;
    setSelectedPhotoIndex(newIndex);
    setSelectedPhoto(productPhotoStore.filteredPhotos[newIndex]);
  };

  const handleNextPhoto = () => {
    const currentIndex = selectedPhotoIndex;
    const newIndex = currentIndex < productPhotoStore.filteredPhotos.length - 1 ? currentIndex + 1 : 0;
    setSelectedPhotoIndex(newIndex);
    setSelectedPhoto(productPhotoStore.filteredPhotos[newIndex]);
  };

  const handleToggleTagGroup = (groupName) => {
    setExpandedTagGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const handleTagToggle = (tagId) => {
    productPhotoStore.toggleTag(tagId);
  };

  const handleClearFilters = () => {
    productPhotoStore.clearFilters();
  };

  // Определяем количество колонок в зависимости от размера экрана
  const getCols = () => {
    if (isMobile) return 1;
    if (isTablet) return 2;
    return 3;
  };

  const getTagGroupNames = () => {
    return Object.keys(productPhotoStore.groupedTags).sort();
  };

  return (
    <>
      {/* Главное модальное окно с галереей */}
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xl"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            height: isMobile ? '100vh' : '90vh',
            maxHeight: isMobile ? '100vh' : '90vh',
          }
        }}>
        
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PhotoLibraryIcon color="primary" />
              <Typography variant="h6" component="div">
                Образцы изделий &quot;{productName}&quot;
              </Typography>
            </Box>
            
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {/* Панель фильтров */}
          {productPhotoStore.photoTags.length > 0 && (
            <Paper sx={{ 
              m: theme.spacing(2), 
              p: theme.spacing(2), 
              bgcolor: theme.palette.background.default,
              border: `1px solid ${theme.palette.divider}`,
            }}>
              {productPhotoStore.hasActiveFilters && (
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'flex-end', 
                  mb: theme.spacing(2) 
                }}>
                  <Button
                    variant="text"
                    size="small"
                    onClick={handleClearFilters}>
                    Сбросить все фильтры
                  </Button>
                </Box>
              )}

              {/* Группы тегов */}
              {getTagGroupNames().map(groupName => (
                <Box key={groupName} sx={{ mb: theme.spacing(2) }}>
                  <Button
                    variant="text"
                    onClick={() => handleToggleTagGroup(groupName)}
                    startIcon={expandedTagGroups[groupName] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    sx={{ 
                      mb: theme.spacing(1), 
                      p: 0, 
                      justifyContent: 'flex-start', 
                      textTransform: 'none' 
                    }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                      {groupName}
                    </Typography>
                  </Button>

                  <Collapse in={expandedTagGroups[groupName] !== false}>
                    <Box sx={{ 
                      display: 'flex', 
                      flexWrap: 'wrap', 
                      gap: theme.spacing(1), 
                      ml: theme.spacing(2) 
                    }}>
                      {productPhotoStore.groupedTags[groupName]?.map(tag => (
                        <Chip
                          key={tag.id}
                          label={tag.name}
                          variant={productPhotoStore.selectedTagIds.includes(tag.id) ? "filled" : "outlined"}
                          color={productPhotoStore.selectedTagIds.includes(tag.id) ? "primary" : "default"}
                          onClick={() => handleTagToggle(tag.id)}
                          size="small"
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Collapse>
                </Box>
              ))}
            </Paper>
          )}

          {/* Загрузка */}
          {productPhotoStore.isLoading && (
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              py: theme.spacing(8) 
            }}>
              <CircularProgress />
            </Box>
          )}

          {/* Ошибка */}
          {productPhotoStore.error && (
            <Alert severity="error" sx={{ m: theme.spacing(2) }}>
              {productPhotoStore.error}
            </Alert>
          )}

          {/* Галерея фотографий */}
          {!productPhotoStore.isLoading && productPhotoStore.hasPhotos && (
            <Box sx={{ p: theme.spacing(2) }}>
              {/* Информация о фильтрации */}
              {productPhotoStore.hasActiveFilters && (
                <Alert severity="info" sx={{ mb: theme.spacing(2) }}>
                  Показано {productPhotoStore.filteredPhotos.length} из {productPhotoStore.photos.length} фотографий
                  {productPhotoStore.filteredPhotos.length === 0 && (
                    <Box sx={{ mt: theme.spacing(1) }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={handleClearFilters}>
                        Сбросить фильтры
                      </Button>
                    </Box>
                  )}
                </Alert>
              )}

              <ImageList cols={getCols()} gap={theme.spacing(2)}>
                {productPhotoStore.filteredPhotos.map((photo) => (
                  <ImageListItem
                    key={photo.id}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'scale(1.02)',
                        '& .photo-overlay': {
                          opacity: 1,
                        }
                      }
                    }}
                    onClick={() => handlePhotoClick(photo)}>
                    
                    <img
                      src={getImageUrl(photo.img_url)}
                      alt={photo.caption || `Образец ${productName}`}
                      loading="lazy"
                      style={{
                        width: '100%',
                        height: 200,
                        objectFit: 'contain',
                        borderRadius: theme.shape.borderRadius,
                        backgroundColor: theme.palette.grey[100],
                      }}
                    />
                    
                    {/* Оверлей с иконкой увеличения */}
                    <Box
                      className="photo-overlay"
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        bgcolor: theme.palette.action.hover,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        opacity: 0,
                        transition: theme.transitions.create('opacity'),
                        borderRadius: theme.shape.borderRadius,
                      }}>
                      <ZoomInIcon sx={{ 
                        color: theme.palette.common.white, 
                        fontSize: 40 
                      }} />
                    </Box>

                    {/* Подпись и теги */}
                    {(photo.caption || (photo.tags && photo.tags.length > 0)) && (
                      <ImageListItemBar
                        title={photo.caption}
                                                  subtitle={
                            photo.tags && photo.tags.length > 0 ? (
                              <Box sx={{ 
                                display: 'flex', 
                                flexWrap: 'wrap', 
                                gap: theme.spacing(0.5), 
                                mt: theme.spacing(0.5) 
                              }}>
                                {photo.tags.slice(0, 3).map(tag => (
                                  <Chip
                                    key={tag.id}
                                    label={tag.name}
                                    size="small"
                                    variant="filled"
                                    sx={{ 
                                      fontSize: '0.7rem',
                                      height: 20,
                                      bgcolor: theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 255, 255, 0.2)' 
                                        : 'rgba(255, 255, 255, 0.8)',
                                      color: theme.palette.mode === 'dark' 
                                        ? theme.palette.common.white 
                                        : theme.palette.text.primary
                                    }}
                                  />
                                ))}
                                {photo.tags.length > 3 && (
                                  <Chip
                                    label={`+${photo.tags.length - 3}`}
                                    size="small"
                                    variant="filled"
                                    sx={{ 
                                      fontSize: '0.7rem',
                                      height: 20,
                                      bgcolor: theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 255, 255, 0.1)' 
                                        : 'rgba(255, 255, 255, 0.6)',
                                      color: theme.palette.text.secondary
                                    }}
                                  />
                                )}
                              </Box>
                            ) : null
                          }
                        position="bottom"
                        sx={{
                          background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.3) 70%, rgba(0,0,0,0) 100%)',
                          '& .MuiImageListItemBar-title': {
                            fontSize: '0.875rem',
                            fontWeight: 'bold',
                          }
                        }}
                      />
                    )}
                  </ImageListItem>
                ))}
              </ImageList>
            </Box>
          )}

          {/* Пустое состояние */}
          {!productPhotoStore.isLoading && !productPhotoStore.hasPhotos && !productPhotoStore.error && (
            <Box sx={{ 
              textAlign: 'center', 
              py: theme.spacing(8) 
            }}>
              <PhotoLibraryIcon sx={{ 
                fontSize: 64, 
                color: theme.palette.text.secondary, 
                mb: theme.spacing(2) 
              }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Фотографии образцов не найдены
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Для этой модели пока нет загруженных фотографий образцов
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="outlined">
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>

      {/* Модальное окно для просмотра увеличенного изображения */}
      <Dialog
        open={isPhotoModalOpen}
        onClose={handleClosePhotoModal}
        maxWidth={false}
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: theme.palette.mode === 'dark' 
              ? 'rgba(0, 0, 0, 0.95)' 
              : 'rgba(0, 0, 0, 0.9)',
            boxShadow: 'none',
            maxWidth: '100vw',
            maxHeight: '100vh',
            margin: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }
        }}>
        <DialogContent sx={{ 
          p: 0, 
          bgcolor: 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          width: '100%',
          height: '100vh',
        }}>
          {selectedPhoto && (
            <>
              {/* Кнопка закрытия */}
              <IconButton
                onClick={handleClosePhotoModal}
                sx={{
                  position: 'fixed',
                  top: theme.spacing(2),
                  right: theme.spacing(2),
                  bgcolor: theme.palette.action.hover,
                  color: theme.palette.common.white,
                  zIndex: theme.zIndex.modal + 1,
                  '&:hover': {
                    bgcolor: theme.palette.action.selected,
                  }
                }}>
                <CloseIcon />
              </IconButton>

              {/* Кнопка "Предыдущее" */}
              {productPhotoStore.filteredPhotos.length > 1 && (
                <IconButton
                  onClick={handlePreviousPhoto}
                  sx={{
                    position: 'fixed',
                    left: theme.spacing(2),
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: theme.palette.action.hover,
                    color: theme.palette.common.white,
                    zIndex: theme.zIndex.modal + 1,
                    '&:hover': {
                      bgcolor: theme.palette.action.selected,
                    }
                  }}>
                  <ArrowBackIcon />
                </IconButton>
              )}

              {/* Кнопка "Следующее" */}
              {productPhotoStore.filteredPhotos.length > 1 && (
                <IconButton
                  onClick={handleNextPhoto}
                  sx={{
                    position: 'fixed',
                    right: theme.spacing(2),
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: theme.palette.action.hover,
                    color: theme.palette.common.white,
                    zIndex: theme.zIndex.modal + 1,
                    '&:hover': {
                      bgcolor: theme.palette.action.selected,
                    }
                  }}>
                  <ArrowForwardIcon />
                </IconButton>
              )}
              
              {/* Центрированное изображение */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                maxWidth: '90vw',
                maxHeight: '90vh',
              }}>
                <img
                  src={getImageUrl(selectedPhoto.img_url)}
                  alt={selectedPhoto.caption || `Образец ${productName}`}
                  style={{
                    maxWidth: '100%',
                    maxHeight: 'calc(90vh - 100px)',
                    objectFit: 'contain',
                    borderRadius: theme.shape.borderRadius,
                    boxShadow: theme.shadows[8],
                  }}
                />
                
                {/* Подпись под увеличенным изображением */}
                {(selectedPhoto.caption || (selectedPhoto.tags && selectedPhoto.tags.length > 0)) && (
                  <Paper sx={{ 
                    mt: theme.spacing(2), 
                    p: theme.spacing(2), 
                    bgcolor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 255, 255, 0.1)' 
                      : 'rgba(255, 255, 255, 0.95)', 
                    maxWidth: '600px',
                    backdropFilter: 'blur(10px)',
                  }}>
                    {selectedPhoto.caption && (
                      <Typography variant="subtitle1" sx={{ 
                        fontWeight: 'bold', 
                        mb: theme.spacing(1), 
                        textAlign: 'center',
                        color: theme.palette.mode === 'dark' 
                          ? theme.palette.common.white 
                          : theme.palette.text.primary
                      }}>
                        {selectedPhoto.caption}
                      </Typography>
                    )}
                    
                    {selectedPhoto.tags && selectedPhoto.tags.length > 0 && (
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: theme.spacing(1), 
                        justifyContent: 'center' 
                      }}>
                        {selectedPhoto.tags.map(tag => (
                          <Chip
                            key={tag.id}
                            label={tag.name}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                      </Box>
                    )}
                  </Paper>
                )}
                
                {/* Индикатор позиции */}
                {productPhotoStore.filteredPhotos.length > 1 && (
                  <Typography variant="caption" sx={{ 
                    color: theme.palette.common.white, 
                    mt: theme.spacing(1),
                    bgcolor: theme.palette.action.hover, 
                    px: theme.spacing(2), 
                    py: theme.spacing(0.5), 
                    borderRadius: theme.shape.borderRadius,
                    backdropFilter: 'blur(10px)',
                  }}>
                    {selectedPhotoIndex + 1} из {productPhotoStore.filteredPhotos.length}
                  </Typography>
                )}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
});

export default ProductPhotoGallery; 